import React, { PropsWithChildren, useRef, memo, useEffect } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import { Identifier } from 'dnd-core';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import Icon, { IconmoonFont } from 'vibo-ui/Icon';

import { DragItem, DragProps } from './interfaces';

import useStyles from './style';

const Drag = <T extends {}>({
  text,
  type,
  index,
  data,
  children,
  selectedIds,
  canDrag,
  dragByHandler,
}: PropsWithChildren<DragProps<T>>) => {
  const classes = useStyles();

  const ref = useRef<HTMLDivElement>(null);
  const handlerRef = useRef<HTMLSpanElement>(null);

  const dataId = get(data, '_id');

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: type,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover: () => null,
  });

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type,
      item: () => ({ index, type, text, data, selectedIds }),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag,
    }),
    [type, text, selectedIds, index]
  );

  drag(drop(dragByHandler ? handlerRef : ref));

  useEffect(() => {
    preview(getEmptyImage());
  }, []);

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={classNames(classes.drag, 'drag', {
        isDragging,
        canDrag,
      })}
      {...(!!dataId ? { 'data-drag-source-id': dataId } : {})}
    >
      {dragByHandler && canDrag ? (
        <Icon icon={IconmoonFont['dnd-16']} className="dndIcon" ref={handlerRef} />
      ) : null}
      {children}
    </div>
  );
};

export default memo(Drag);
